/**
 * Search engines card — the ONE place a project connects Google Search
 * Console and Bing Webmaster Tools, and sees what IndexNow is doing.
 *
 * One button per engine. Everything else (proving ownership, registering the
 * property, submitting the sitemap) happens server-side in
 * lib/seo/engine-setup.ts, and this card only shows the state machine:
 *
 *   not_connected → verifying → connected
 *                             ↘ failed  (manual fallback opens itself)
 *
 * The manual token fields live under "Advanced" and open automatically ONLY
 * when automation has given up — nobody is asked to paste a token first.
 * Replaces the former Verification tab (/projects/:id/seo).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Crown,
  Loader2,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers, type ProjectSeoConfig } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { qk } from '@/lib/queryKeys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConfirm } from '@/components/ui/confirm-provider';

export type SearchEngine = 'google' | 'bing';
export type EngineStatus = 'not_connected' | 'verifying' | 'connected' | 'failed';

export interface EngineSetupState {
  attempts: number;
  firstAttemptAt?: string;
  lastAttemptAt?: string;
  tokenInstalled?: boolean;
  verifiedAt?: string;
  sitemapSubmittedAt?: string;
  lastErrorCode?: string;
  lastError?: string;
  platformFault?: boolean;
  gaveUpAt?: string;
}

export interface EngineView {
  available: boolean;
  status: EngineStatus;
  email: string | null;
  siteUrl: string | null;
  connectedAt: string | null;
  tokenInstalled: boolean;
  setup: EngineSetupState | null;
  message: string | null;
}

export interface SearchEnginesData {
  plan: string;
  origin: string | null;
  google: EngineView;
  bing: EngineView;
  indexNow: {
    active: boolean;
    key: string | null;
    autoSubmit: {
      lastRunAt?: string;
      urlsInSitemap?: number;
      lastSitemapSubmitAt?: string;
      lastSitemapSubmitOk?: boolean;
      lastIndexNowAt?: string;
      lastIndexNowOk?: boolean;
      lastIndexNowUrls?: number;
      inspectedToday?: number;
      lastError?: string;
    } | null;
  };
}

const ENGINE_NAME: Record<SearchEngine, string> = {
  google: 'Google Search Console',
  bing: 'Bing Webmaster Tools',
};
const ENGINE_SHORT: Record<SearchEngine, string> = { google: 'Google', bing: 'Bing' };

/** A check attempted in the last 3 minutes reads as "in progress", not "waiting". */
const IN_PROGRESS_WINDOW_MS = 3 * 60 * 1000;

function isInProgress(view: EngineView | undefined, now: number): boolean {
  if (!view || view.status !== 'verifying') return false;
  const last = view.setup?.lastAttemptAt ? new Date(view.setup.lastAttemptAt).getTime() : 0;
  return now - last < IN_PROGRESS_WINDOW_MS && !view.setup?.lastErrorCode?.startsWith('PLATFORM');
}

/** Shared by the card and the dashboard (which gates reports on Google being connected). */
export function useSearchEngines(projectId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: qk.seoEngines(projectId),
    queryFn: async () => {
      const res = await apiHelpers.seoCopilotEngines(projectId!);
      return (res.data as any)?.data as SearchEnginesData;
    },
    enabled: Boolean(projectId) && enabled,
    refetchInterval: (query) => {
      const d = query.state.data as SearchEnginesData | undefined;
      if (!d) return false;
      const now = Date.now();
      if (isInProgress(d.google, now) || isInProgress(d.bing, now)) return 4000;
      if (d.google.status === 'verifying' || d.bing.status === 'verifying') return 60_000;
      return false;
    },
  });
}

function fmtWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString();
}

export function SearchEnginesCard({ projectId, isPro }: { projectId: string; isPro: boolean }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isPending } = useSearchEngines(projectId, true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.seoEngines(projectId) });

  // Landing from an engine's OAuth round-trip: say what happened, once.
  useEffect(() => {
    const engine = searchParams.get('engine') as SearchEngine | null;
    const result = searchParams.get('result');
    if (!engine || !result) return;
    const name = ENGINE_SHORT[engine] ?? engine;
    if (result === 'connected') toast.success(`${name} connected and verified.`);
    else if (result === 'verifying') toast.info(`${name} connected — confirming ownership…`);
    else if (result === 'cancelled') {
      toast.warning(`${name} sign-in was cancelled. Nothing changed.`);
    } else if (result === 'failed') {
      toast.error(`${name} connected, but setup could not finish. See the card.`);
    } else toast.error(`${name} sign-in did not complete. Try again.`);
    const next = new URLSearchParams(searchParams);
    next.delete('engine');
    next.delete('result');
    setSearchParams(next, { replace: true });
    invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('engine'), searchParams.get('result')]);

  const connectMutation = useMutation({
    mutationFn: async (engine: SearchEngine) => {
      const res = await apiHelpers.seoCopilotEngineAuthorize(projectId, engine);
      return (res.data as any)?.data?.authorizeUrl as string | undefined;
    },
    onSuccess: (url) => {
      if (url) window.location.href = url;
      else toast.error('Could not start sign-in');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Connect failed')),
  });

  const setupMutation = useMutation({
    mutationFn: async (engine: SearchEngine) => {
      const res = await apiHelpers.seoCopilotEngineSetup(projectId, engine);
      return (res.data as any)?.data as { status: EngineStatus; message: string | null };
    },
    onSuccess: (d, engine) => {
      invalidate();
      if (d?.status === 'connected') toast.success(`${ENGINE_SHORT[engine]} verified.`);
      else toast.info(d?.message || `${ENGINE_SHORT[engine]}: still waiting for confirmation.`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Check failed')),
  });

  const disconnectMutation = useMutation({
    mutationFn: (engine: SearchEngine) => apiHelpers.seoCopilotEngineDisconnect(projectId, engine),
    onSuccess: (_r, engine) => {
      invalidate();
      toast.success(`${ENGINE_SHORT[engine]} disconnected.`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Disconnect failed')),
  });

  // Manual fallback tokens (Advanced).
  const { data: tokens } = useQuery({
    queryKey: qk.seoTokens(projectId),
    queryFn: async () => {
      const res = await apiHelpers.getProjectSeo(projectId);
      return (res.data.data || {}) as ProjectSeoConfig;
    },
    enabled: advancedOpen,
  });
  const [googleToken, setGoogleToken] = useState('');
  const [bingToken, setBingToken] = useState('');
  useEffect(() => {
    if (tokens) {
      setGoogleToken(tokens.googleSiteVerification || '');
      setBingToken(tokens.bingSiteVerification || '');
    }
  }, [tokens]);
  const saveTokenMutation = useMutation({
    mutationFn: (patch: Partial<ProjectSeoConfig>) => apiHelpers.updateProjectSeo(projectId, patch),
    onSuccess: () => {
      toast.success('Saved — the file is being served now.');
      queryClient.invalidateQueries({ queryKey: qk.seoTokens(projectId) });
      invalidate();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to save')),
  });

  const anyFailed = data?.google.status === 'failed' || data?.bing.status === 'failed';
  useEffect(() => {
    if (anyFailed) setAdvancedOpen(true);
  }, [anyFailed]);

  const now = Date.now();
  const origin = data?.origin ?? null;
  const originHost = useMemo(() => (origin ? origin.replace(/^https?:\/\//, '') : null), [origin]);

  if (isPending || !data) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading search engines…
      </div>
    );
  }

  const canConnect = isPro && Boolean(origin);

  const renderEngine = (engine: SearchEngine) => {
    const view = data[engine];
    if (!view.available && engine === 'bing') return null; // not configured on the platform → never shown broken
    const name = ENGINE_NAME[engine];
    const short = ENGINE_SHORT[engine];
    const busy =
      connectMutation.isPending || setupMutation.isPending || disconnectMutation.isPending;
    const inProgress = isInProgress(view, now);
    const platformFault = Boolean(view.setup?.platformFault);
    const reconnect =
      view.setup?.lastErrorCode === 'RECONNECT_REQUIRED' ||
      view.setup?.lastErrorCode === 'NO_TOKEN';

    const letter = (
      <div className="bg-background flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border text-base font-bold">
        {engine === 'google' ? 'G' : 'B'}
      </div>
    );

    if (view.status === 'not_connected') {
      return (
        <div key={engine} className="flex items-center gap-4 rounded-xl border p-4">
          {letter}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {name}
              {engine === 'bing' && (
                <span className="text-muted-foreground rounded-full border px-2 py-0.5 text-[11px] font-medium">
                  Optional
                </span>
              )}
            </div>
            <div className="text-muted-foreground text-xs">
              {engine === 'google'
                ? 'Ownership · Search Console property · sitemap · queries, coverage, URL inspection'
                : 'Ownership · site · sitemap · Bing queries and crawl issues. Bing already receives every URL via IndexNow.'}
            </div>
          </div>
          <Button
            onClick={() => connectMutation.mutate(engine)}
            disabled={busy || !canConnect || !view.available}
            variant={engine === 'google' ? 'default' : 'outline'}
            title={
              !view.available
                ? `${short} is not configured on this platform yet`
                : !origin
                  ? 'Add a custom domain first'
                  : !isPro
                    ? 'Requires Pro'
                    : undefined
            }
          >
            {connectMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Connect {short}
          </Button>
        </div>
      );
    }

    if (view.status === 'connected') {
      return (
        <div
          key={engine}
          className="flex items-center gap-4 rounded-xl border border-green-200 bg-green-50/60 p-3 dark:border-green-900/50 dark:bg-green-950/20"
        >
          {letter}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="text-sm font-semibold">{short}</span>
            {view.email && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> {view.email}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Verified
            </span>
            {view.siteUrl && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />{' '}
                {engine === 'google' ? 'Property' : 'Site'} {view.siteUrl}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              {view.setup?.sitemapSubmittedAt ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Sitemap submitted{' '}
                  {fmtWhen(view.setup.sitemapSubmittedAt)}
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Sitemap not accepted yet
                  — re-sent hourly
                </>
              )}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-xs">
            <button
              onClick={() => setupMutation.mutate(engine)}
              disabled={busy}
              className="text-primary inline-flex items-center gap-1 hover:underline disabled:opacity-50"
            >
              {setupMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Re-run checks
            </button>
            <button
              onClick={async () => {
                if (
                  await confirm({
                    title: `Disconnect ${short}?`,
                    message: 'Reports stop until you reconnect. Ownership files stay in place.',
                    danger: true,
                  })
                ) {
                  disconnectMutation.mutate(engine);
                }
              }}
              disabled={busy}
              className="text-muted-foreground hover:underline disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </div>
      );
    }

    // verifying | failed
    const failed = view.status === 'failed';
    const tone = platformFault
      ? 'border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/20'
      : 'border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20';
    const headline = platformFault
      ? 'Automatic verification is unavailable right now'
      : reconnect
        ? `Reconnect ${short} to finish setup`
        : failed
          ? `${short} has not confirmed you own ${originHost ?? 'this site'}`
          : inProgress
            ? `Setting up ${short}…`
            : `Waiting for ${short} to confirm ownership`;
    return (
      <div key={engine} className={`rounded-xl border p-4 ${tone}`}>
        <div className="flex items-start gap-4">
          {letter}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
              {name}
              {view.email && (
                <span className="text-muted-foreground font-normal">· {view.email}</span>
              )}
            </div>
            <div className="mt-1 text-sm font-medium">{headline}</div>
            {inProgress ? (
              <ul className="mt-2 space-y-1 text-xs">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> {short} account connected
                </li>
                <li className="flex items-center gap-2">
                  {view.tokenInstalled ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Ownership file installed on your site
                </li>
                <li className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Asking {short} to confirm
                  ownership — usually a few seconds
                </li>
                <li className="text-muted-foreground flex items-center gap-2">
                  <span className="inline-block h-3.5 w-3.5 rounded-full border" />{' '}
                  {engine === 'google' ? 'Add to Search Console' : 'Register in Bing'} and submit
                  the sitemap
                </li>
              </ul>
            ) : (
              <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                {view.message ||
                  `The ownership file is live on your site. We re-check every hour and show the result here.`}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {reconnect ? (
                <Button size="sm" onClick={() => connectMutation.mutate(engine)} disabled={busy}>
                  Reconnect {short}
                </Button>
              ) : (
                <>
                  <Button size="sm" onClick={() => setupMutation.mutate(engine)} disabled={busy}>
                    {setupMutation.isPending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    )}
                    Retry now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => connectMutation.mutate(engine)}
                    disabled={busy}
                  >
                    Connect a different {short} account
                  </Button>
                </>
              )}
              <button
                onClick={async () => {
                  if (
                    await confirm({
                      title: `Disconnect ${short}?`,
                      message: 'You can reconnect any time. Ownership files stay in place.',
                      danger: true,
                    })
                  ) {
                    disconnectMutation.mutate(engine);
                  }
                }}
                disabled={busy}
                className="text-muted-foreground self-center text-xs hover:underline disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const auto = data.indexNow.autoSubmit;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium">Search engines</h2>
          <p className="text-muted-foreground text-sm">
            Connect each engine once. We prove you own the site, register it, submit your sitemap
            and start pulling reports — nothing to copy, paste or download.
          </p>
        </div>
        {!isPro && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            <Crown className="h-3 w-3" /> Requires Pro
          </span>
        )}
      </div>

      {!isPro && (
        <div className="rounded-xl border p-4 text-sm">
          <div className="font-medium">Search engine reports are part of Pro</div>
          <p className="text-muted-foreground mt-1 text-xs">
            See the exact queries you rank for on Google and Bing, your index coverage, and get
            every new page submitted automatically.{' '}
            <Link to={`/projects/${projectId}/plugins/seo-copilot?tab=plans`} className="underline">
              See plans →
            </Link>
          </p>
        </div>
      )}

      {isPro && !origin && (
        <div className="rounded-xl border p-4 text-sm">
          <div className="font-medium">Add your domain first</div>
          <p className="text-muted-foreground mt-1 text-xs">
            Search engines verify a real domain. Connect yours under Domains, then come back — each
            engine takes about 10 seconds after that.{' '}
            <Link to={`/projects/${projectId}?tab=domains`} className="underline">
              Go to Domains →
            </Link>
          </p>
        </div>
      )}

      {renderEngine('google')}
      {renderEngine('bing')}

      {/* IndexNow — already automated; shown so the promise is visible. */}
      <div className="flex items-center gap-4 rounded-xl border border-green-200 bg-green-50/60 p-3 dark:border-green-900/50 dark:bg-green-950/20">
        <div className="bg-background flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
          <Zap className="h-4 w-4 text-green-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            New-page submission (IndexNow)
            <span className="rounded-full border border-green-300 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:border-green-800 dark:text-green-400">
              {data.indexNow.active ? 'Active · nothing to do' : 'Activates on first run'}
            </span>
          </div>
          <div className="text-muted-foreground text-xs">
            {auto?.lastIndexNowAt ? (
              <>
                {auto.lastIndexNowUrls ?? 0} URLs sent to Bing, Yandex, Naver and Seznam{' '}
                {fmtWhen(auto.lastIndexNowAt)}
                {auto.lastIndexNowOk === false ? ' (last ping rejected — retried hourly)' : ''}
                {auto.lastSitemapSubmitAt
                  ? ` · Google: sitemap ${auto.lastSitemapSubmitOk ? 'sent' : 'not accepted'} ${fmtWhen(auto.lastSitemapSubmitAt)}`
                  : ''}
                {typeof auto.inspectedToday === 'number'
                  ? ` · ${auto.inspectedToday} URLs inspected today`
                  : ''}
              </>
            ) : (
              'Every new or changed page is sent to Bing, Yandex, Naver and Seznam within the hour, and instantly when a blog post publishes. Google is told through the sitemap once connected above.'
            )}
          </div>
        </div>
      </div>

      {/* Advanced — manual fallback + read-only keys. Opens itself on failure. */}
      <div className="rounded-xl border">
        <button
          onClick={() => setAdvancedOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm"
        >
          <span className="inline-flex items-center gap-2">
            {advancedOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Advanced — ownership files and IndexNow key
          </span>
          <span className="text-muted-foreground text-xs">
            {anyFailed ? 'Manual verification' : 'Only needed if automation fails'}
          </span>
        </button>
        {advancedOpen && (
          <div className="space-y-5 border-t px-4 py-4 text-sm">
            <div className="space-y-2">
              <div className="font-medium">
                Verify Google manually{data.google.status === 'failed' ? ' instead' : ''}
              </div>
              <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-xs leading-relaxed">
                <li>
                  Open{' '}
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    Search Console
                  </a>{' '}
                  and add the property <code>{origin ? `${origin}/` : 'https://your-domain/'}</code>{' '}
                  (URL prefix).
                </li>
                <li>
                  Pick the <strong>HTML file</strong> method. Google shows a filename like{' '}
                  <code>google1234abcd5678ef.html</code>.
                </li>
                <li>
                  Paste the part between <code>google</code> and <code>.html</code> below, save,
                  then press <strong>Verify</strong> in Search Console. We serve the file.
                </li>
              </ol>
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="google-token" className="text-xs">
                    Token from the filename
                  </Label>
                  <Input
                    id="google-token"
                    value={googleToken}
                    onChange={(e) => setGoogleToken(e.target.value.trim())}
                    placeholder="1234abcd5678ef"
                    className="font-mono"
                    maxLength={64}
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => saveTokenMutation.mutate({ googleSiteVerification: googleToken })}
                  disabled={saveTokenMutation.isPending}
                >
                  Save and serve
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="font-medium">Verify Bing manually</div>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Open{' '}
                <a
                  href="https://www.bing.com/webmasters"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Bing Webmaster Tools
                </a>
                , add <code>{origin || 'https://your-domain'}</code>, pick <strong>XML file</strong>
                , and paste the code from the <code>BingSiteAuth.xml</code> it shows. We serve the
                file. Quickest alternative inside Bing:{' '}
                <strong>Import from Google Search Console</strong>, once Google is verified.
              </p>
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="bing-token" className="text-xs">
                    Code from BingSiteAuth.xml
                  </Label>
                  <Input
                    id="bing-token"
                    value={bingToken}
                    onChange={(e) => setBingToken(e.target.value.trim())}
                    placeholder="258CAD36B9EEE22F1CFDEB4C239D26BB"
                    className="font-mono"
                    maxLength={64}
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => saveTokenMutation.mutate({ bingSiteVerification: bingToken })}
                  disabled={saveTokenMutation.isPending}
                >
                  Save and serve
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">
                IndexNow key (auto-generated, served at /&lt;key&gt;.txt at the site root)
              </Label>
              <Input
                value={data.indexNow.key || tokens?.indexNowKey || ''}
                readOnly
                className="font-mono"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
